<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status get-height">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;relatórios de reembolso para faturamento.
          </div>

          <div class="table__header__container" :class="{ 'table__header__container--pr': hasVerticalScrollbar }">
            <v-layout class="table__header" align-center>
              <v-flex xs1>{{ $t(`${namespace}.downloads`) }}</v-flex>
              <v-flex xs1>{{ $t(`${namespace}.id`) }}</v-flex>
              <v-flex xs1>{{ $t(`${namespace}.date`) }}</v-flex>
              <v-flex xs2>{{ $t(`${namespace}.amount`) }}</v-flex>
              <v-flex xs1>{{ $t(`${namespace}.invoices`) }}</v-flex>
              <v-flex xs2 />
              <v-flex xs2>{{ $t(`${namespace}.discounts`) }}</v-flex>
              <v-flex xs2>{{ $t(`${namespace}.chargeback`) }}</v-flex>
            </v-layout>

            <transition name="fade">
              <div
                v-if="activeGroupDate"
                v-html="activeGroupDate"
                class="table__header__date"
              />
            </transition>
          </div>

          <div class="table__body__container" :style="`height: ${tableHeight}px`" @scroll="tableScroll">
            <v-list
              class="table__body"
              subheader
              two-line
            >
              <div v-for="(data, i) in items" :key="`tr-${i}`" class="table__group">
                <div
                  v-html="dateHtmlFormated(data.date)"
                  :data-date="data.date"
                  class="table__date"
                />

                <div v-for="(item, i) in data.list" :key="`item-${i}`">
                  <v-list-tile class="table__body__tr">
                    <v-list-tile-content>
                      <v-list-tile-sub-title>
                        <v-layout align-center>
                          <v-flex xs1>
                            <v-tooltip slot="append" min-width="138" max-width="138" top>
                              <v-btn
                                v-if="item.orderSummaryUrl"
                                :href="item.orderSummaryUrl"
                                color="gray-6"
                                class="ma-0"
                                slot="activator"
                                flat
                                icon
                              >
                                <v-icon>description</v-icon>
                              </v-btn>
                              <span>{{ $t(`${namespace}.summaryTooltip`) }}</span>
                            </v-tooltip>

                            <v-tooltip slot="append" min-width="138" max-width="138" top>
                              <v-btn
                                v-if="item.cityHallUrl"
                                :href="item.cityHallUrl"
                                color="gray-6"
                                class="ma-0"
                                slot="activator"
                                flat
                                icon
                              >
                                <v-icon>receipt</v-icon>
                              </v-btn>
                              <span>{{ $t(`${namespace}.receiptTooltip`) }}</span>
                            </v-tooltip>
                          </v-flex>

                          <v-flex xs1>
                            <span :title="item.numericalId " class="text--ellipsis" contenteditable>
                              {{ item.numericalId }}
                            </span>
                          </v-flex>

                          <v-flex xs1>
                            <span :title="item.date | formatDate('DD/MM/YYYY')" class="text--ellipsis">
                              {{ item.date | formatDate('DD/MM/YYYY') }}
                            </span>
                          </v-flex>

                          <v-flex xs2>
                            <strong :title="item.subtotal | currencyBRLwithoutSymbol" class="text--ellipsis">
                              {{ item.subtotal | currencyBRLwithoutSymbol }}
                            </strong>
                          </v-flex>

                          <v-flex xs1>
                            <strong :title="item.totalOfOrders | formatNumber" class="text--ellipsis">
                              {{ item.totalOfOrders | formatNumber }}
                            </strong>
                          </v-flex>

                          <v-flex xs2 class="text--ellipsis">
                            <span
                              v-if="item.totalOfPaymentMethodBoleto"
                              :title="$t(`${namespace}.billets`)"
                              class="text--ellipsis"
                            >
                              {{ item.totalOfPaymentMethodBoleto | formatNumber }} {{ $t(`${namespace}.billets`) }}
                            </span>

                            <span
                              v-if="item.totalOfPaymentMethodCreditCard"
                              :title="$t(`${namespace}.creditCards`)"
                              class="text--ellipsis"
                            >
                              {{ item.totalOfPaymentMethodCreditCard | formatNumber }} {{ $t(`${namespace}.creditCards`) }}
                            </span>

                            <span
                              v-if="item.totalOfPaymentMethodBankTransfer"
                              :title="$t(`${namespace}.bankTransfers`)"
                              class="text--ellipsis"
                            >
                              {{ item.totalOfPaymentMethodBankTransfer | formatNumber }} {{ $t(`${namespace}.bankTransfers`) }}
                            </span>
                          </v-flex>

                          <v-flex xs2>
                            <span :title="item.discountAmount | currencyBRLwithoutSymbol" class="text--ellipsis">
                              {{ item.discountAmount | currencyBRLwithoutSymbol }}
                            </span>
                          </v-flex>

                          <v-flex xs1>
                            <span :title="item.chargeBackAmount | currencyBRLwithoutSymbol" class="text--ellipsis">
                              {{ item.chargeBackAmount | currencyBRLwithoutSymbol }}
                            </span>
                          </v-flex>
                        </v-layout>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>

                  <v-divider />
                </div>
              </div>
            </v-list>
          </div>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneFound`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    }
  },
  components: {
    EmptyState
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    headerPageHeight () {
      const elems = document.querySelectorAll('.get-height')
      const calc = Array.from(elems).reduce((acc, item, arr) => {
        acc = acc + item.offsetHeight
        return acc
      }, 0)

      return calc
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.RevenuesPage',
      activeGroupDate: null,
      tableHeight: 0,
      hasVerticalScrollbar: false
    }
  },
  methods: {
    dateHtmlFormated (str) {
      return str ? str.replace(/(\d{2}\/\d{2}\/\d{4})(.+)/, '<strong>$1</strong>,$2') : null
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    calcTableHeight () {
      const filterHeight = 69
      this.tableHeight = window.innerHeight - (this.headerPageHeight + filterHeight + 95)
    },

    tableScroll (event) {
      const wrapper = event.target
      const containerDateHeight = 37
      const filterHeight = 69

      Array.from(document.querySelectorAll('.table__date')).forEach(($item, index) => {
        if (wrapper.scrollTop <= 0) {
          this.activeGroupDate = null
          return
        }

        if ((wrapper.scrollTop - containerDateHeight) >= ($item.offsetTop - this.headerPageHeight - filterHeight)) {
          this.activeGroupDate = this.dateHtmlFormated($item.dataset.date)
        }
      })
    },

    checkIfElementHasVerticalScrollbar () {
      setTimeout(() => {
        const div = document.querySelector('.table__body__container')
        if (div) this.hasVerticalScrollbar = div.scrollHeight > div.clientHeight
      }, 100)
    }
  },
  watch: {
    items (val) {
      this.calcTableHeight()
      this.checkIfElementHasVerticalScrollbar()
    }
  },
  mounted () {
    this.calcTableHeight()
    this.checkIfElementHasVerticalScrollbar()
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () {
    window.addEventListener('resize', this.calcTableHeight)
  }
}
</script>

<style lang="scss" scoped>
.table__header__container {
  background-color: #F0F0F0;
  position: relative;

  &.table__header__container--pr {
    padding-right: 16px;
  }
}

.table__header__date {
  background-color: #fff;
  left: 0;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1;
}

.table__date {
  background-color: $gray-1;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  color: $gray-7;
  font-size: 14px;
  line-height: normal;
  padding: 10px 16px;
}

.table__header__date {
  background-color: #fff;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  color: $gray-7;
  font-size: 14px;
  line-height: normal;
  padding: 10px 16px;
}

.table__body__container {
  height: auto;
  overflow-y: auto;
}
</style>
